import { formatAddressInline, formatAddressLines } from "../countries"

type PropertyUnitLike = {
  id?: unknown
  unit_label?: unknown
  label?: unknown
  unit_number?: unknown
  unit_type?: unknown
}

type PropertyWithUnitsLike = {
  units?: PropertyUnitLike[] | null
}

const normalizeText = (value: unknown) => {
  if (value === null || value === undefined) return ""

  return String(value).trim()
}

export const findPropertyUnitById = (
  property: PropertyWithUnitsLike | undefined | null,
  propertyUnitId?: string | null,
) => {
  const normalizedId = normalizeText(propertyUnitId)

  if (!normalizedId) return null

  return (property?.units ?? []).find((unit) => normalizeText(unit.id) === normalizedId) ?? null
}

const formatPropertyUnitRawIdentifier = (unit: PropertyUnitLike | undefined | null) =>
  normalizeText(unit?.unit_label) || normalizeText(unit?.label) || normalizeText(unit?.unit_number)

/** Display legacy/imported unit names consistently without changing the stored identifier. */
export const formatPropertyUnitLabel = (value: unknown, unitType?: string | null): string => {
  const text = normalizeText(value)
  const building = unitType === "BUILDING" || /^(?:Gebäudeeinheit|Gebäude)(?=\s|:|$)/iu.test(text)

  if (building) {
    const name = text.replace(/^(?:Gebäudeeinheit|Gebäude)(?:\s*:\s*)?/iu, "").trim()

    return !name || /^(?:Gebäude|Gebäudeeinheit)$/iu.test(name)
      ? "Gebäudeeinheit"
      : `Gebäudeeinheit: ${name}`
  }

  const identifier = text.replace(/^(?:Wohnung|Wohneinheit)(?=\s|:|$)\s*:?\s*/iu, "").trim()

  if (!identifier) return "Wohneinheit"

  const number = identifier.replace(/^Nr\.?\s*/iu, "")

  if (/^Nr\.?\s*/iu.test(identifier) || /^\d/u.test(number)) return `Wohneinheit Nr. ${number}`

  return `Wohneinheit ${identifier}`
}

export const formatPropertyUnitIdentifier = (unit: PropertyUnitLike | undefined | null) =>
  formatPropertyUnitLabel(formatPropertyUnitRawIdentifier(unit), normalizeText(unit?.unit_type))

export const formatPropertyUnitDisplay = (
  unit: PropertyUnitLike | undefined | null,
  fallback?: string | null,
) =>
  formatPropertyUnitLabel(
    formatPropertyUnitRawIdentifier(unit) || fallback,
    normalizeText(unit?.unit_type),
  )

export const splitPropertyUnitDisplayLines = (unitLabel?: string | null) =>
  normalizeText(unitLabel)
    .split(/\s*[·×]\s*/u)
    .map(normalizeText)
    .filter(Boolean)

export const formatPropertyUnitDisplayForProperty = (
  property: PropertyWithUnitsLike | undefined | null,
  propertyUnitId?: string | null,
  fallback?: string | null,
) => {
  const unit = findPropertyUnitById(property, propertyUnitId)

  if (unit) return formatPropertyUnitDisplay(unit, fallback)

  const fallbackText = normalizeText(fallback)

  if (fallbackText) return formatPropertyUnitLabel(fallbackText)

  return normalizeText(propertyUnitId) ? "Wohneinheit" : null
}

export const formatPropertyAddressLinesWithUnit = (
  property: Property | undefined | null,
  propertyUnitId?: string | null,
  fallback?: string | null,
) => {
  if (!property) return ["Unbekannte Liegenschaft"]

  return [
    ...formatAddressLines(property),
    ...splitPropertyUnitDisplayLines(
      formatPropertyUnitDisplayForProperty(property, propertyUnitId, fallback),
    ),
  ]
}

export const formatPropertyAddressInlineWithUnit = (
  property: Property | undefined | null,
  propertyUnitId?: string | null,
  fallback?: string | null,
) => {
  if (!property) return ""

  return formatAddressInline(
    property,
    formatPropertyUnitDisplayForProperty(property, propertyUnitId, fallback),
  )
}

export const formatPropertyUnitSelectLabel = (property: Property, unit: PropertyUnit) => {
  const address = formatAddressInline(property) || "Ohne Liegenschaft"

  return `${formatPropertyUnitDisplay(unit)} - ${address}`
}
